import React, { FC, useState } from 'react';
import { FilterAttributeGroup, AttributeFilterOptionTemplate, FilterTypeName } from 'store/category/types';
import getCategoryFilters from 'store/category/selector/getCategoryFilters';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import OpRadio from 'components/Common/op-ui-wrappers/OpRadio/OpRadio';
import gtmSendEvent, { gtmChangeSortingEvent, gtmSortSelectEvent } from 'utils/gtm/gtmSendEvent';
import { setCategoryRefetchFlagAction } from 'store/category/slice';
import setCategorySortByOperation from 'store/category/operation/setCategorySortByOperation';
import updateCategoryQueryStringOperation from 'store/category/operation/updateCategoryQueryStringOperation';
import { useHistory } from 'react-router';
import getNumberOfAppliedFilters from 'store/category/selector/getNumberOfAppliedFilters';
import getCategorySortBy from 'store/category/selector/getCategorySortBy';
import setCategoryPageNrOperation from 'store/category/operation/setCategoryPageNrOperation';
import OpIcon from 'components/Common/op-ui-wrappers/OpIcon/OpIcon';
import styles from './Filters.module.scss';
import CheckBoxOptions from '../ItemsFilter/CheckBoxOptions/CheckBoxOptions';
import Filter from './Filter';

export type FiltersProps = {
  showTitle?: boolean;
  applyFiltersImmediately?: boolean;
  showSorting?: boolean;
};

const Filters: FC<FiltersProps> = ({ showTitle = true, applyFiltersImmediately, showSorting }) => {
  const { t } = useTranslation();
  const categoryFilter = useSelector(getCategoryFilters);
  const dispatch = useDispatch();
  const history = useHistory();
  const sortBy = useSelector(getCategorySortBy);
  const numberOfAppliedFilters = useSelector(getNumberOfAppliedFilters);
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  const filterPresent = (optionTemplate: AttributeFilterOptionTemplate) => {
    return (
      categoryFilter?.tagFilters.some((el) => el.optionTemplate === optionTemplate) ||
      categoryFilter?.attributeFilters.some((el) => el.optionTemplate === optionTemplate)
    );
  };

  const sortOptions = [
    {
      title: t('txt_sort_by_popular_and_new'),
      value: 6,
    },
    {
      title: t('txt_sort_by_most_popular'),
      value: 1,
    },
    {
      title: t('txt_sort_by_new'),
      value: 2,
    },
  ];

  const changeSortBy = (sort: number) => {
    gtmSendEvent(gtmChangeSortingEvent(sort));
    gtmSendEvent(gtmSortSelectEvent(sort));
    dispatch(setCategorySortByOperation(sort));
    dispatch(setCategoryPageNrOperation(1));
    dispatch(setCategoryRefetchFlagAction(true));
    dispatch(updateCategoryQueryStringOperation(history));
  };

  const renderSorting = () => {
    if (!showSorting) return null;

    return (
      <>
        <div className={styles.sortHeading}>
          <OpText variant="body-large-bold" className={styles.filterName}>
            {t('txt_sort_by')}
          </OpText>
        </div>
        <div className={styles.sortOptions}>
          {sortOptions.map((option) => (
            <OpRadio
              key={option.title + option.value}
              className={styles.dropdownOption}
              checked={sortBy === option.value ? true : undefined}
              onClick={() => changeSortBy(option.value)}
            >
              <OpText slot="heading" variant="body-large-regular">
                {option.title}
              </OpText>
            </OpRadio>
          ))}
        </div>
      </>
    );
  };

  return (
    <>
      {showTitle && (
        <div onClick={() => setFiltersExpanded(!filtersExpanded)}>
          <OpText variant="title-large-bold" className={styles.title}>
            {t('txt_SearchFilters')} {numberOfAppliedFilters ? `(${numberOfAppliedFilters})` : ''}
            <OpIcon icon={filtersExpanded ? 'caret-up' : 'caret-down'} size="x-small" />
          </OpText>
        </div>
      )}
      {renderSorting()}
      {filtersExpanded && (
        <div className={styles.filters}>
          {filterPresent(AttributeFilterOptionTemplate.BlockValueOption) && (
            <div className={styles.sizes}>
              <div className={styles.filterName}>{t('txt_size')}</div>
              <div className={styles.sizesContainer}>
                {categoryFilter?.tagFilters?.map(
                  (attribute, index) =>
                    attribute.optionTemplate === AttributeFilterOptionTemplate.BlockValueOption &&
                    attribute.typeName !== FilterTypeName.orientation && (
                      <CheckBoxOptions
                        key={`cbot${index}`}
                        attribute={attribute}
                        group={FilterAttributeGroup.tagFilters}
                      />
                    ),
                )}
                {categoryFilter?.attributeFilters?.map(
                  (attribute, index) =>
                    attribute.optionTemplate === AttributeFilterOptionTemplate.BlockValueOption &&
                    attribute.typeName !== FilterTypeName.orientation && (
                      <CheckBoxOptions
                        key={`cboa${index}`}
                        attribute={attribute}
                        group={FilterAttributeGroup.attributeFilters}
                      />
                    ),
                )}
              </div>
            </div>
          )}

          {/* Product type filter */}
          {categoryFilter?.attributeFilters
            .filter((attribute) => attribute.typeName === 'ProductType')
            .map((attribute, index) => (
              <Filter
                key={index}
                attribute={attribute}
                index={index}
                filterAttributeGroup={FilterAttributeGroup.attributeFilters}
                applyFiltersImmediately={applyFiltersImmediately}
              />
            ))}

          {/* Orientation filter */}
          {categoryFilter?.attributeFilters
            .filter((attribute) => attribute.typeName === 'Orientation')
            .map((attribute, index) => (
              <Filter
                key={index}
                attribute={attribute}
                index={index}
                filterAttributeGroup={FilterAttributeGroup.attributeFilters}
                applyFiltersImmediately={applyFiltersImmediately}
              />
            ))}

          {/* Color filter */}
          {categoryFilter?.tagFilters
            .filter((attribute) => attribute.typeName === 'color')
            .map((attribute, index) => (
              <Filter
                key={index}
                attribute={attribute}
                index={index}
                filterAttributeGroup={FilterAttributeGroup.tagFilters}
                applyFiltersImmediately={applyFiltersImmediately}
              />
            ))}
        </div>
      )}
    </>
  );
};

export default Filters;
